import * as React from 'react';

import {RouteComponentProps} from 'react-router';
import Page from './Page';
import { Header, Breadcrumb } from 'semantic-ui-react';
import {Link} from 'react-router-dom';

interface Props {
	id?: string;
}

interface State {
}

export default class MissionPage extends Page<RouteComponentProps<Props>, State> {

	protected isLoading (): any {
		return false;
	}

	protected renderTitle (): string {
		return 'Brain Brooder - Mission';
	}

	protected renderDescription (): string {
		return 'Improving education through personalization, rooting out weaknesses, and a focus on the long term.';
	}

	protected renderContent () {
		const sections = [{ key: 'Home', content: <Link to={'/'}>Home</Link> }, { key: 'Mission', content: 'Mission', active: true }];

		return (
			<div className={'lesson'}>
				<Breadcrumb icon='right angle' sections={sections} />

				<Header dividing as='h1' inverted>Mission</Header>

				<p style={{fontSize: '27px', color: '#F2711C'}}>
					Brain Brooder's mission is to accelerate progress in science and technology, and in understanding ourselves and others.
				</p>

				<p style={{fontSize: '27px'}}>
					We aim to do this by improving education using some simple changes outlined in the plan below.
				</p>

				<Header inverted dividing as='h2' style={{fontSize: '1.5em', marginTop: '64px'}}>Plan</Header>

				<h2 style={{color: '#F2711C'}}>Focus on lifelong mastery</h2>

				<p>
					Once a skill or knowledge is acquired, it does not need to be forgotten.
					Yet this is exactly what is happening on a continuing basis.
					We learn psychology and biology, and then, even if we ace our exam, forget most of it within a year.
				</p>

				<p>
					We already know that this problem can be efficiently mitigated using a spaced repetition system(SRS).
					Using an SRS, things we have trouble with will be ruthlessly repeated until we don't,
					and things we know well will be reminded to us just often enough not to forget them.
					Brain Brooder aims to integrate an SRS with our core curricula,
					such that we'll still remember how our mind and our cells function when we are 80 years old.
				</p>

				<h2 style={{color: '#F2711C'}}>Taking the optimal path</h2>

				<p>
					If there are 50 students taking a course, it is very difficult to adjust the course to all of their needs.
					Inevitably, some of the students are underprepared and will face excessive difficulty,
					while others waste their time being bored while hearing an explanation for the third time.
				</p>

				<p>
					If we would have a map of the gaps in knowledge in the former group, and of the already known territory of the latter group,
					then we can plan a personal route for each of them, reducing stress and boredom, while increasing joy and effectiveness.
				</p>

				<p>
					This is why we aim to create a knowledge map, where every lesson shows its dependency on the knowledge gained in other lessons.
					In this way, when you start a course, you will clearly see which lessons you will need to follow as prerequisites,
					and which lessons in the course you can safely skip without missing anything.
				</p>

				<h2 style={{color: '#F2711C'}}>Free, high quality education that is always up to date</h2>

				<p>
					Wikipedia has shown us that it is possible for a large crowd of people to create a free high quality encyclopedia,
					as long as the right mechanisms are in place to guard this quality.
					We plan to apply the same mechanism to education.
				</p>

				<p>
					We will start by offering free courses that are either created by ourselves, or by OpenStax.
					Then, when we have a significant userbase, we will ask users to suggest improvements and new lessons.
					All changes will first be monitored by us, and over time this control will be delegated to a growing group of users that are known to have made good quality contributions.
					The aim is to end up with an extensive knowledge map that covers many areas both widely and deeply.
				</p>

				<h2 style={{color: '#F2711C'}}>Teachers as advisers and confidants</h2>

				<p>
					As digital education is becoming better and more personalized, the approach of instructing entire classrooms at once is becoming obsolete.
					The most important capacities of teachers are those capacities that cannot be substituted digitally.
					This means that the value of teachers will consist mostly of spending one on one time with their students, helping them determine their learning paths,
					offering guidance, keeping them motivated, and being there for them in case they have any personal problems.
				</p>

				<h2 style={{color: '#F2711C'}}>A more relevant core curriculum</h2>

				<p>
					Until now, we have only been able to teach those subjects for which we had knowledgeable teachers.
					As more education becomes digitized, and the role of teachers changes, this no longer needs to be true.
					We can change the curriculum away from what our teachers can teach, to what is most useful for our students to learn,
					which may include for example how to communicate well, how to manage their personal finances, and how to understand themselves and others better.
				</p>

				<h2 style={{color: '#F2711C'}}>More freedom to choose, and more differentiation</h2>

				<p>
					As the amount of digitized knowledge expands, we can let students start to explore the fields they are most interested in earlier in life.
					In this way, we can keep students more motivated, as well as more differentiated,
					such that our schools will produce graduates that are not all bored and identical, but that possess a wide array of competencies, and an unstilled hunger for knowledge.
				</p>
			</div>
		);
	}
}


// WEBPACK FOOTER //
// ./code/project/pages/MissionPage.tsx